import { useAxios } from '../useAxios';
import { AxiosError } from 'axios';
import { ReactQueryConfig } from 'hooks/hooks.types';
import { useSnackbar } from 'notistack';
import { useMemo } from 'react';
import { UseQueryResult, useQuery } from 'react-query';
import { keysToCamelCase } from 'utils/formatters';

export function useAxiosQuery<R>(
  key: (string | { filters: string })[],
  path: string,
  { method = 'GET', body = null, mapper, ...reactQueryConfig }: ReactQueryConfig<R> = {}
): UseQueryResult<R> {
  const { enqueueSnackbar } = useSnackbar();
  const { axiosRequest } = useAxios();

  const fetchData = async () => {
    let data;
    const response = await axiosRequest({ method, path, body });

    data = response?.data;

    if (mapper) {
      data = mapper(data);
    }

    return keysToCamelCase(data) as R;
  };

  const { data, ...args } = useQuery<R, AxiosError>(key, () => fetchData(), {
    onError: (error) => {
      enqueueSnackbar(`Something went wrong with getting data from the database: ${error?.message}`, {
        variant: 'error',
      });
    },
    retry: 2,
    ...reactQueryConfig,
  });

  const transformedKeyData = useMemo(() => {
    if (!data) {
      return [];
    } else {
      return keysToCamelCase(data);
    }
  }, [data]);

  return {
    data: transformedKeyData,
    ...args,
  };
}
